/**
 * <LtrNumber> + <LtrAmount> + <LtrText> — force LTR direction on Latin
 * / numeric content inside an RTL context (Arabic-sprint PR 4 +
 * M30.5.0.1).
 *
 * Numbers and Latin identifiers always read left-to-right regardless
 * of surrounding text direction. In an Arabic page, `1,234.56 SAR`
 * rendered as plain text inherits RTL and visually breaks
 * ("SAR 56.234,1" or worse, with Arabic-Indic digit substitution).
 * Same problem with URLs: `https://riyadh.solvait.ai` contains
 * neutral characters (`:`, `/`, `.`) that follow surrounding bidi
 * context, so in an RTL container they cluster on the wrong side
 * and the visual order falls apart ("//:https" / "solvait.ai.").
 *
 * Wrap with these primitives to keep Latin content LTR:
 *
 *   <LtrNumber value={1234.56} />
 *   <LtrAmount value={60035.94} currency="SAR" />
 *   <LtrText>https://</LtrText>
 *   <LtrText>riyadh@solvait.ai</LtrText>
 *   <LtrText>/api/transactions/leave</LtrText>
 *
 * `<bdi>` (Bidi Isolation) is the right HTML primitive — it isolates
 * its content from the surrounding bidi algorithm so the content
 * stays LTR even inside an Arabic paragraph.
 */

import type { ReactNode } from "react";

export interface LtrNumberProps {
  /** The number (or pre-formatted string) to render. */
  value: number | string;
  /** Locale for Intl.NumberFormat. Defaults to "en-US" so digits
   *  render in Western Arabic numerals (0-9), not Arabic-Indic
   *  (٠-٩). Override only if the consumer truly wants AR digits. */
  formatLocale?: string;
  /** Intl.NumberFormat options. */
  options?: Intl.NumberFormatOptions;
  /** Optional className passthrough. */
  className?: string;
}

export function LtrNumber({
  value,
  formatLocale = "en-US",
  options,
  className,
}: LtrNumberProps): ReactNode {
  const formatted =
    typeof value === "number"
      ? new Intl.NumberFormat(formatLocale, options).format(value)
      : value;
  return (
    <bdi className={className} dir="ltr">
      {formatted}
    </bdi>
  );
}

export interface LtrAmountProps {
  value: number;
  currency: string; // "SAR" / "USD" etc.
  formatLocale?: string;
  /** Minimum fraction digits (default 2 for currency). */
  minimumFractionDigits?: number;
  className?: string;
}

export function LtrAmount({
  value,
  currency,
  formatLocale = "en-US",
  minimumFractionDigits = 2,
  className,
}: LtrAmountProps): ReactNode {
  const formatted = new Intl.NumberFormat(formatLocale, {
    style: "currency",
    currency,
    minimumFractionDigits,
  }).format(value);
  return (
    <bdi className={className} dir="ltr">
      {formatted}
    </bdi>
  );
}

/**
 * <LtrText> — render arbitrary Latin/ASCII content inside an RTL
 * paragraph without bidi reordering. Use for:
 *
 *   - URLs (`https://`, `solvait.ai`, the workspace URL slug)
 *   - Email addresses (`user@solvait.test`)
 *   - File / API paths (`/api/transactions/leave`)
 *   - Mixed Latin+digit identifiers (employee codes like RI-001,
 *     contract numbers, IBAN segments)
 *   - Code-like content (CR numbers, GOSI IDs, version strings)
 *
 * Wraps children in `<bdi dir="ltr">` so the bidi algorithm
 * isolates them from the surrounding paragraph. Anything that
 * "looks Latin" but is rendered inside an Arabic paragraph
 * benefits from this primitive — without it, neutral characters
 * (slashes, dots, colons, hyphens) follow surrounding direction
 * and the visual order falls apart.
 *
 * For numeric values prefer `<LtrNumber>` (handles Intl.NumberFormat
 * + Western digit guarantees). For currency values prefer
 * `<LtrAmount>`. Use `<LtrText>` for everything else.
 */
export interface LtrTextProps {
  children: ReactNode;
  className?: string;
}

export function LtrText({
  children,
  className,
}: LtrTextProps): ReactNode {
  return (
    <bdi className={className} dir="ltr">
      {children}
    </bdi>
  );
}
